'use client';

import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { cn } from '@shared/utils/utils';
import { useTranslation } from 'react-i18next';
import type { LegacyDashboardFilters, BooleanFilter, MigrationFilter } from '../../models/Filters';
import { DEFAULT_LEGACY_DASHBOARD_FILTERS } from '../../models/Filters';
import { countActiveLegacyDashboardFilters } from '../../utils/applyLegacyDashboardFilters';

interface LegacyDashboardFiltersPanelProps {
  filters: LegacyDashboardFilters;
  onChange: (filters: LegacyDashboardFilters) => void;
  paymentProfileOptions: { value: string; label: string }[];
  shopChannelOptions: { value: string; label: string }[];
  countryOptions: { value: string; label: string }[];
  migrationStatusOptions: { value: string; label: string }[];
  className?: string;
}

export const LegacyDashboardFiltersPanel = ({
  filters,
  onChange,
  paymentProfileOptions,
  shopChannelOptions,
  countryOptions,
  migrationStatusOptions,
  className,
}: LegacyDashboardFiltersPanelProps) => {
  const { t } = useTranslation('legacyDashboardModule');
  const activeFiltersCount = countActiveLegacyDashboardFilters(filters);

  const update = (patch: Partial<LegacyDashboardFilters>) => {
    onChange({ ...filters, ...patch });
  };

  const clearAllFilters = () => {
    onChange(DEFAULT_LEGACY_DASHBOARD_FILTERS);
  };

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection title={t('filters.sections.migration')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.migration')}>
            <Select
              value={filters.migrationFilter}
              onValueChange={(value) => update({ migrationFilter: value as MigrationFilter })}
            >
              <SelectTrigger className="h-[38px] text-sm" data-testid="legacy-dashboard-filter-migration">
                <SelectValue placeholder={t('filters.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.all')}</SelectItem>
                <SelectItem value="migrated">{t('filters.migrated')}</SelectItem>
                <SelectItem value="not_migrated">{t('filters.not_migrated')}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>

          <FilterField label={t('filters.migration_run_status')}>
            <MultiSelect
              options={migrationStatusOptions}
              value={filters.migrationRunStatuses}
              onValueChange={(migrationRunStatuses) =>
                update({ migrationRunStatuses: migrationRunStatuses as LegacyDashboardFilters['migrationRunStatuses'] })
              }
              placeholder={t('filters.all_statuses')}
              emptyText={t('filters.notFound')}
              disabled={migrationStatusOptions.length === 0}
              dataTestId="legacy-dashboard-filter-migration-status"
              triggerClassName="h-[38px]"
            />
          </FilterField>
        </div>
      </FilterSection>

      <FilterSection title={t('filters.sections.volume')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <NumericRangeField
            label={t('filters.orders_count')}
            minValue={filters.minOrdersCount}
            maxValue={filters.maxOrdersCount}
            minPlaceholder={t('filters.min')}
            maxPlaceholder={t('filters.max')}
            onMinChange={(minOrdersCount) => update({ minOrdersCount })}
            onMaxChange={(maxOrdersCount) => update({ maxOrdersCount })}
            minTestId="legacy-dashboard-filter-orders-min"
            maxTestId="legacy-dashboard-filter-orders-max"
          />
          <NumericRangeField
            label={t('filters.avg_orders_per_month')}
            minValue={filters.minAvgOrdersPerMonth}
            maxValue={filters.maxAvgOrdersPerMonth}
            minPlaceholder={t('filters.min')}
            maxPlaceholder={t('filters.max')}
            onMinChange={(minAvgOrdersPerMonth) => update({ minAvgOrdersPerMonth })}
            onMaxChange={(maxAvgOrdersPerMonth) => update({ maxAvgOrdersPerMonth })}
            minTestId="legacy-dashboard-filter-avg-min"
            maxTestId="legacy-dashboard-filter-avg-max"
          />
        </div>
      </FilterSection>

      <FilterSection title={t('filters.sections.business')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.payment_profile')}>
            <MultiSelect
              options={paymentProfileOptions}
              value={filters.paymentProfiles}
              onValueChange={(paymentProfiles) =>
                update({ paymentProfiles: paymentProfiles as LegacyDashboardFilters['paymentProfiles'] })
              }
              placeholder={t('filters.all_payment_profiles')}
              emptyText={t('filters.notFound')}
              dataTestId="legacy-dashboard-filter-payment-profile"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.shop_channels')}>
            <MultiSelect
              options={shopChannelOptions}
              value={filters.shopChannels}
              onValueChange={(shopChannels) => update({ shopChannels })}
              placeholder={t('filters.all_channels')}
              emptyText={t('filters.notFound')}
              disabled={shopChannelOptions.length === 0}
              dataTestId="legacy-dashboard-filter-channels"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.country_code')}>
            <MultiSelect
              options={countryOptions}
              value={filters.countryCodes}
              onValueChange={(countryCodes) => update({ countryCodes })}
              placeholder={t('filters.all_countries')}
              emptyText={t('filters.notFound')}
              disabled={countryOptions.length === 0}
              dataTestId="legacy-dashboard-filter-country"
              triggerClassName="h-[38px]"
            />
          </FilterField>
        </div>
      </FilterSection>

      <FilterSection title={t('filters.sections.flags')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <BooleanFilterField
            label={t('filters.verified')}
            value={filters.verified}
            onChange={(verified) => update({ verified })}
            t={t}
            testId="legacy-dashboard-filter-verified"
          />
          <BooleanFilterField
            label={t('filters.is_dropshipping')}
            value={filters.isDropshipping}
            onChange={(isDropshipping) => update({ isDropshipping })}
            t={t}
            testId="legacy-dashboard-filter-dropshipping"
          />
          <BooleanFilterField
            label={t('filters.crm_active')}
            value={filters.crmActive}
            onChange={(crmActive) => update({ crmActive })}
            t={t}
            testId="legacy-dashboard-filter-crm"
          />
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            {t('filters.clear', { count: activeFiltersCount })}
          </Button>
        </div>
      )}
    </div>
  );
};

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
}

const FilterSection = ({ title, children }: FilterSectionProps) => (
  <div className="space-y-3">
    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</h3>
    {children}
  </div>
);

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

interface BooleanFilterFieldProps {
  label: string;
  value: BooleanFilter;
  onChange: (value: BooleanFilter) => void;
  t: (key: string) => string;
  testId: string;
}

const BooleanFilterField = ({ label, value, onChange, t, testId }: BooleanFilterFieldProps) => (
  <FilterField label={label}>
    <Select value={value} onValueChange={(next) => onChange(next as BooleanFilter)}>
      <SelectTrigger className="h-[38px] text-sm" data-testid={testId}>
        <SelectValue placeholder={t('filters.boolean_all')} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('filters.boolean_all')}</SelectItem>
        <SelectItem value="yes">{t('table.yes')}</SelectItem>
        <SelectItem value="no">{t('table.no')}</SelectItem>
      </SelectContent>
    </Select>
  </FilterField>
);

interface NumericRangeFieldProps {
  label: string;
  minValue: number | null;
  maxValue: number | null;
  minPlaceholder: string;
  maxPlaceholder: string;
  onMinChange: (value: number | null) => void;
  onMaxChange: (value: number | null) => void;
  minTestId: string;
  maxTestId: string;
}

const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const NumericRangeField = ({
  label,
  minValue,
  maxValue,
  minPlaceholder,
  maxPlaceholder,
  onMinChange,
  onMaxChange,
  minTestId,
  maxTestId,
}: NumericRangeFieldProps) => (
  <FilterField label={label}>
    <div className="grid grid-cols-2 gap-2">
      <Input
        type="number"
        min={0}
        step="any"
        value={minValue ?? ''}
        placeholder={minPlaceholder}
        onChange={(event) => onMinChange(parseOptionalNumber(event.target.value))}
        className="h-[38px] text-sm"
        data-testid={minTestId}
      />
      <Input
        type="number"
        min={0}
        step="any"
        value={maxValue ?? ''}
        placeholder={maxPlaceholder}
        onChange={(event) => onMaxChange(parseOptionalNumber(event.target.value))}
        className="h-[38px] text-sm"
        data-testid={maxTestId}
      />
    </div>
  </FilterField>
);
